import { createContext, use, useMemo } from 'react'
import { useStore as useZustandStore } from 'zustand'
import { createStore } from 'zustand/vanilla'

export type EditablePolicy = {
  forceMode?: 'read' | 'edit'
  allowUndo: boolean
  revertExit: 'onBlur' | 'debounced' | 'immediate'
  revertDebounceMs: number
  exitOnBlur: boolean
  exitOnEnter: boolean
}

export type EditableFormMeta = {
  mode?: 'edit' | 'create'
  lastSavedAt?: number
  submitErrors: string[]
}

export type EditableFormState = {
  policy: EditablePolicy
  meta: EditableFormMeta
  setPolicy: (next: Partial<EditablePolicy>) => void
  setMeta: (next: Partial<EditableFormMeta>) => void
  clearSubmitErrors: () => void
}

export const defaultPolicy: EditablePolicy = {
  forceMode: undefined,
  allowUndo: true,
  revertExit: 'onBlur',
  revertDebounceMs: 600,
  exitOnBlur: false,
  exitOnEnter: false,
}

export const defaultMeta: EditableFormMeta = {
  submitErrors: [],
}

export const fallbackEditableStore = createStore<EditableFormState>(() => ({
  policy: { ...defaultPolicy, forceMode: 'edit', allowUndo: false },
  meta: defaultMeta,
  setPolicy: () => {},
  setMeta: () => {},
  clearSubmitErrors: () => {},
}))

export const EditableFormContext = createContext<ReturnType<typeof createStore<EditableFormState>> | null>(null)

export type EditableFormProviderProps = {
  initialPolicy?: Partial<EditablePolicy>
  initialMeta?: Partial<EditableFormMeta>
}

export const useEditableForm = ({ initialPolicy, initialMeta }: EditableFormProviderProps) => {
  const forceMode = initialPolicy?.forceMode
  const allowUndo = forceMode === 'edit' ? false : (initialPolicy?.allowUndo ?? defaultPolicy.allowUndo)
  const revertExit = initialPolicy?.revertExit ?? defaultPolicy.revertExit
  const revertDebounceMs = initialPolicy?.revertDebounceMs ?? defaultPolicy.revertDebounceMs
  const exitOnBlur = initialPolicy?.exitOnBlur ?? defaultPolicy.exitOnBlur
  const exitOnEnter = initialPolicy?.exitOnEnter ?? defaultPolicy.exitOnEnter
  const mode = initialMeta?.mode
  const lastSavedAt = initialMeta?.lastSavedAt
  const submitErrors = initialMeta?.submitErrors ?? defaultMeta.submitErrors
  const submitErrorsKey = JSON.stringify(submitErrors)

  return useMemo(
    () =>
      createStore<EditableFormState>(set => ({
        policy: {
          forceMode,
          allowUndo,
          revertExit,
          revertDebounceMs,
          exitOnBlur,
          exitOnEnter,
        },
        meta: { mode, lastSavedAt, submitErrors: JSON.parse(submitErrorsKey) },
        setPolicy: next => set(s => ({ ...s, policy: { ...s.policy, ...next } })),
        setMeta: next => set(s => ({ ...s, meta: { ...s.meta, ...next } })),
        clearSubmitErrors: () => set(s => ({ ...s, meta: { ...s.meta, submitErrors: [] } })),
      })),
    [forceMode, allowUndo, revertExit, revertDebounceMs, exitOnBlur, exitOnEnter, mode, lastSavedAt, submitErrorsKey],
  )
}

export function useEditableFormStore() {
  const ctx = use(EditableFormContext)
  if (!ctx) throw new Error('useEditableFormStore must be used within EditableFormProvider')
  return ctx
}

export function useEditableFormState<T>(selector: (s: EditableFormState) => T, equalityFn?: (a: T, b: T) => boolean) {
  const store = useEditableFormStore()
  return useZustandStore(store, selector, equalityFn)
}

export function useEditableFormStateOptional<T>(
  selector: (s: EditableFormState) => T,
  equalityFn?: (a: T, b: T) => boolean,
) {
  const store = use(EditableFormContext) ?? fallbackEditableStore
  return useZustandStore(store, selector, equalityFn)
}

export function useEditablePolicy() {
  const policy = useEditableFormState(s => s.policy)
  const setPolicy = useEditableFormState(s => s.setPolicy)
  return { policy, setPolicy }
}
